import { Component, Input, ViewEncapsulation } from '@angular/core';
import { Employee } from '../../../models/employee';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuxiliarService } from '../../../services/auxiliar.service';
import { NGXLogger } from 'ngx-logger';
import { TranslateService } from '@ngx-translate/core';
import { AbsenceService } from '../../../services/absence.service';
import { dateAfterValidator } from '../../../utils/validators/dateAfterValidator';
import _ from 'lodash';
import moment from 'moment';
import { Absence } from '../../../models/absence';

type DrawerPosition = 'left' | 'right';

@Component({
  selector: 'app-absence-drawer',
  standalone: false,
  templateUrl: './absence-drawer.component.html',
  styleUrl: './absence-drawer.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class AbsenceDrawerComponent {
  @Input() absence: Absence | undefined;
  @Input() employee: Employee | undefined;
  @Input() openAbsence: Absence | undefined;
  @Input() position: DrawerPosition;

  public absenceTypes: {name: string, value: string}[] = [];
  public allDayOptions: {name: string, value: boolean}[] = [];
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public form: FormGroup;
  public moment = moment;

  get f(): { [index: string]: any } {
    return this.form.controls;
  }

  constructor(
    private formBuilder: FormBuilder,
    private auxiliarService: AuxiliarService,
    private logger: NGXLogger,
    private translate: TranslateService,
    private absenceService: AbsenceService,
  ){}

  ngOnInit(): void {
    // all day options
    this.allDayOptions = this.auxiliarService.getBinaryOptions();
    // get absence types
    this.absenceTypes = _.orderBy(this.auxiliarService.getAbsenceTypes(), 'name');
    // load form
    this.loadForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private loadForm(): void {
    this.form = this.formBuilder.group({
      begin: [this.absence?.begin || '', [Validators.required]],
      beginHour: [this.absence?.isAllDay || !this.absence?.begin ? '' : this.absence?.begin, []],
      comments: [this.absence?.comments || '', []],
      employee: [{
        value: _.get(this.employee, 'givenname', ''), 
        disabled: true
      }, []],
      end: [this.absence?.isAllDay && this.absence?.end ? this.absence?.end : '', []],
      endHour: [this.absence?.isAllDay ? '' : this.absence?.end, []],
      isAllDay: [this.absence?.isAllDay || false, []],
      type: [_.find(this.absenceTypes, {value: this.absence?.type ?? 'sickness'}), [Validators.required]],
    });

    this.f['begin'].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.f['end'].updateValueAndValidity();
      });

    // all day absence value change
    this.f['isAllDay'].valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: boolean) => {
        if(value) {
          this.disableAllDay();
        } else {
          this.enableAllDay();
        }
      });

    // not all day by default
    this.f['isAllDay'].setValue(this.absence?.isAllDay ?? false);
    
    // disable fields in absence edition
    if(this.absence) {
      this.f['begin'].disable();
      this.f['isAllDay'].disable();
      this.f['type'].disable();
      if(this.absence.isAllDay && this.absence.end) {
        this.f['end'].disable();
      } else {
        this.f['beginHour'].disable();
        this.f['endHour'].disable();
      }
    }
  }

  private disableAllDay(): void {
    // values
    this.f['beginHour'].setValue(this.f['beginHour'].value || new Date(0, 0, 0, 0, 0, 0), { emitEvent: false });
    this.f['endHour'].setValue(this.f['endHour'].value || new Date(0, 0, 0, 0, 0, 0));
    // enable / disable
    this.f['beginHour'].disable();
    this.f['endHour'].disable();
    this.f['end'].enable();
    // validators
    this.f['beginHour'].setValidators([]);
    this.f['endHour'].setValidators([]);
    this.f['end'].setValidators([dateAfterValidator(this.f['begin'])]);
    // update
    this.f['beginHour'].updateValueAndValidity();
    this.f['endHour'].updateValueAndValidity();
    this.f['end'].updateValueAndValidity();
  }

  private enableAllDay(): void {
    // values
    this.f['end'].setValue('');
    // enable / disable
    this.f['beginHour'].enable();
    this.f['endHour'].enable();
    this.f['end'].disable();
    // validators
    this.f['beginHour'].setValidators([Validators.required]);
    this.f['endHour'].setValidators([Validators.required]);
    this.f['end'].setValidators([]);
    // update
    this.f['beginHour'].updateValueAndValidity();
    this.f['endHour'].updateValueAndValidity();
    this.f['end'].updateValueAndValidity();
  }

  public save(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const formData = _.cloneDeep(this.form.getRawValue());
      _.set(formData, 'type', _.get(formData, 'type.value', formData.type));
      if(formData.isAllDay) {
        delete formData.beginHour;
        delete formData.endHour;
        if(!formData.end) delete formData.end;
      } else {
        _.set(formData, 'begin', this.auxiliarService.timeTransform(moment(formData.beginHour).format('HH:mm:ss'), _.clone(formData.begin)));
        _.set(formData, 'end', this.auxiliarService.timeTransform(moment(formData.endHour).format('HH:mm:ss'), _.clone(formData.begin)));
      }
      delete formData.beginHour;
      delete formData.endHour;
      delete formData.employee;
      if(this.absence) {
        _.set(formData, 'id', this.absence.id);
        this.absenceService.updateAbsence(formData, this.employee).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: (res: any) => {
            this.logger.debug('[absenceService] updateAbsence res: ', res);
            this.auxiliarService.toast(this.translate.instant('success.absence-update'), 'success');
            this.auxiliarService.closeDrawer(this.position, _.get(res, 'body'));
          },
          error: (err: any) => {
            this.logger.error('[absenceService] updateAbsence err:', err);
            this.auxiliarService.toast(this.translate.instant('error.absence-update'), 'error');
          }
        });
      } else {
        this.absenceService.createAbsence(formData, this.employee).pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: (res: any) => {
            this.logger.debug('[absenceService] createAbsence res: ', res);
            this.auxiliarService.toast(this.translate.instant('success.absence-create'), 'success');
            this.auxiliarService.closeDrawer(this.position, _.get(res, 'body'));
          },
          error: (err: any) => {
            this.logger.error('[absenceService] createAbsence err:', err);
            this.auxiliarService.toast(this.translate.instant('error.absence-create'), 'error');
          }
        });
      }
    }
  }

  public cancel(): void {
    this.auxiliarService.closeDrawer(this.position);
  }

  public remove(): void {
    if(!this.absence) return;
    this.absenceService.deleteAbsence(this.absence, this.employee).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res: any) => {
        this.logger.debug('[absenceService] deleteAbsence res: ', res);
        this.auxiliarService.closeDrawer(this.position, _.get(res, 'body'));
      },
      error: (err: any) => {
        this.logger.error('[absenceService] deleteAbsence err:', err);
        this.auxiliarService.toast(this.translate.instant('error.absence-delete'), 'error');
      }
    });
  }
}
